import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  ArrowUpRight,
  Ship,
  Users,
  Sparkles,
  Tag,
  Clock,
} from 'lucide-react';
import axios from 'axios';
import { earlyBirdInfo } from '../../lib/earlyBird';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photos réelles des destinations depuis sognudimare.com
const destinationPhotos = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_0ce032b8fe2e4bf58652c8d18e897478~mv2.jpg/v1/fill/w_900,h_500,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO%20copie.jpg',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_98fe44ee0ba0419794505afe2c70aa95~mv2.jpg/v1/fill/w_900,h_500,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO.jpg',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_f3bee630d0c549388bbc71c0e58fb9ea~mv2.jpg/v1/fill/w_900,h_650,al_c,q_80,enc_avif,quality_auto/sognudimare%20a%20scandola_edited.jpg',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_9e15d4c9779b4d48ad56a689ab2fe02b~mv2.avif/v1/fill/w_900,h_860,al_c,q_80,enc_avif,quality_auto/LAGOON%2043%20SOGNUDIMARE.avif',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_227bd245ee9c48eca3a0f527080bf3a7~mv2.jpg/v1/fill/w_900,h_580,al_c,q_80,enc_avif,quality_auto/cephalonie%20grece.jpg',
  'croisiere-catamaran-grece-cyclades-2-semaines': '/images/grece_cyclades_hero.jpg',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_3eb608d9c85e4effadf3d95d064cac02~mv2.webp/v1/fill/w_900,h_600,al_c,q_80,enc_avif,quality_auto/grenadines-a-la-decouverte-des-grenadines_482185_panohd.webp',
};

const formatShortDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const countByYear = (dates, year) => {
  if (!dates) return 0;
  return dates.filter((d) => (d.start_date || '').startsWith(String(year))).length;
};

const nextDeparture = (dates, year) => {
  if (!dates) return null;
  const now = new Date();
  return dates
    .filter((d) => {
      if (year && !(d.start_date || '').startsWith(String(year))) return false;
      return new Date(d.start_date) > now && d.status !== 'full';
    })
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];
};

// ────────────────────────────────────────────────────────
// Tarif Anticipé banner (5% si réservation au moins 10 mois avant)
// ────────────────────────────────────────────────────────
const EarlyBirdBanner = () => (
  <div
    className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#0A2540] via-[#1D3557] to-[#0A2540] border border-[#C4724F]/30 mb-12 sm:mb-16"
    data-testid="early-bird-banner"
  >
    {/* Decorative blob */}
    <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#C4724F]/20 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-[#2A9D8F]/15 rounded-full blur-3xl pointer-events-none" />

    <div className="relative px-5 py-6 sm:px-10 sm:py-8 flex flex-col lg:flex-row items-start lg:items-center gap-5 lg:gap-8">
      {/* Big -5% badge */}
      <div className="shrink-0 flex items-center gap-4">
        <div className="relative">
          <div className="absolute inset-0 bg-[#C4724F] rounded-2xl blur-md opacity-60 animate-pulse" />
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#C4724F] to-[#A0552F] rounded-2xl flex flex-col items-center justify-center text-white shadow-2xl rotate-[-4deg]">
            <Sparkles className="h-4 w-4 mb-0.5 opacity-90" />
            <div className="font-display text-3xl sm:text-4xl font-bold leading-none">−5%</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-90">Tarif Anticipé</div>
          </div>
        </div>
      </div>

      {/* Texte principal */}
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#2A9D8F]/20 border border-[#2A9D8F]/40 rounded-full mb-2">
          <Clock className="h-3 w-3 text-[#3AB8A8]" />
          <span className="text-[#3AB8A8] text-[10px] sm:text-xs font-semibold uppercase tracking-wider">
            Réservation anticipée
          </span>
        </div>
        <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-1.5">
          Réservez tôt, économisez{' '}
          <span className="italic text-[#C4956A]">−5 %</span>
        </h3>
        <p className="text-white/75 text-sm sm:text-base leading-relaxed">
          Bénéficiez de <span className="font-semibold text-white">5 % de remise</span> sur le prix
          de votre cabine ou privatisation pour toute réservation effectuée{' '}
          <span className="text-[#3AB8A8] font-semibold">au moins 10 mois avant le départ</span>.
        </p>
      </div>

      {/* CTA */}
      <Link
        to="/destinations-croisieres-catamaran"
        className="shrink-0 group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C4724F] hover:bg-[#B0613F] text-white font-semibold text-sm shadow-xl shadow-[#C4724F]/30 transition-all hover:scale-[1.02] w-full lg:w-auto"
        data-testid="early-bird-cta"
      >
        Profiter de l'offre
        <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </Link>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────
// Carte croisière moderne
// ────────────────────────────────────────────────────────
const CruiseCard = ({ cruise }) => {
  const photoUrl =
    destinationPhotos[cruise.slug] ||
    cruise.images?.[0] ||
    'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=900';

  const count2027 = countByYear(cruise.dates, 2027);
  const next2027 = nextDeparture(cruise.dates, 2027);
  const hasEligibleDate = (cruise.dates || []).some(
    (d) => earlyBirdInfo(d.start_date).eligible
  );

  return (
    <article
      className="group relative bg-white rounded-3xl overflow-hidden border border-slate-200/60 shadow-[0_4px_20px_-8px_rgba(10,37,64,0.08)] hover:shadow-[0_20px_50px_-12px_rgba(10,37,64,0.18)] transition-all duration-500 flex flex-col"
      data-testid={`cruise-card-${cruise.slug}`}
    >
      {/* Photo */}
      <div className="relative overflow-hidden aspect-[4/3] sm:aspect-[5/4]">
        <img
          src={photoUrl}
          alt={cruise.destination}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
          loading="lazy"
        />
        {/* Bottom dark fade for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/85 via-[#0A2540]/15 to-transparent" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {cruise.is_cabin_booking && (
              <span className="px-2.5 py-1 bg-[#2A9D8F] text-white text-[10px] sm:text-xs font-semibold rounded-full backdrop-blur-sm shadow-sm">
                Cabine
              </span>
            )}
            {cruise.is_privatization && (
              <span className="px-2.5 py-1 bg-white/95 text-[#0A2540] text-[10px] sm:text-xs font-semibold rounded-full shadow-sm">
                Privatisation
              </span>
            )}
          </div>
          {hasEligibleDate && (
            <span
              className="shrink-0 px-2.5 py-1 bg-[#C4724F] text-white text-[10px] sm:text-xs font-bold rounded-full shadow-md flex items-center gap-1"
              data-testid={`card-earlybird-${cruise.slug}`}
            >
              <Tag className="h-3 w-3" />
              −5 % Tarif Anticipé
            </span>
          )}
        </div>

        {/* Bottom overlay : destination + name */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-white">
          <div className="flex items-center gap-1.5 text-[#C4956A] text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] mb-1.5">
            <MapPin className="h-3 w-3" />
            {cruise.destination}
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">
            {cruise.name}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-5 sm:p-6 flex flex-col">
        <p className="text-[#4A5568] text-sm leading-relaxed mb-4 line-clamp-2">
          {cruise.short_description || cruise.description}
        </p>

        {/* Mini info row */}
        <div className="grid grid-cols-3 gap-2 text-center pb-4 mb-4 border-b border-slate-100">
          <div>
            <Calendar className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Durée</div>
            <div className="text-xs font-semibold text-[#0A2540]">{cruise.duration}</div>
          </div>
          <div>
            <Ship className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Départ</div>
            <div className="text-xs font-semibold text-[#0A2540] truncate">
              {cruise.departure_port}
            </div>
          </div>
          <div>
            <Users className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Pax</div>
            <div className="text-xs font-semibold text-[#0A2540]">
              {cruise.max_passengers || 8} max
            </div>
          </div>
        </div>

        {/* Planning 2027 cell */}
        <div className="mb-5">
          <YearCell
            year="2027"
            count={count2027}
            next={next2027}
            color="terracotta"
            discount
          />
        </div>

        {/* Price + CTA */}
        <div className="mt-auto flex items-end justify-between gap-3">
          <div>
            {cruise.base_price > 0 ? (
              <>
                <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">
                  À partir de
                </div>
                <div className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] leading-none">
                  {cruise.base_price}€
                </div>
                <div className="text-[10px] text-[#94A3B8]">par passager</div>
              </>
            ) : (
              <>
                <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">
                  Privatisation dès
                </div>
                <div className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] leading-none">
                  {cruise.privatization_base_price}€
                </div>
              </>
            )}
          </div>
          <Link
            to={`/${cruise.slug}`}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 bg-[#0A2540] hover:bg-[#102E54] text-white text-sm font-semibold rounded-full shadow-md hover:shadow-xl transition-all group/btn"
            data-testid={`reserve-${cruise.slug}`}
          >
            Réserver
            <ArrowUpRight className="h-4 w-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const YearCell = ({ year, count, next, color, discount }) => {
  const colors = {
    slate: {
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      label: 'text-slate-500',
      year: 'text-[#0A2540]',
      count: 'text-[#2A9D8F]',
    },
    terracotta: {
      bg: 'bg-gradient-to-br from-[#FFF7F2] to-[#FFEDE0]',
      border: 'border-[#C4724F]/25',
      label: 'text-[#C4724F]',
      year: 'text-[#0A2540]',
      count: 'text-[#C4724F]',
    },
  };
  const c = colors[color] || colors.slate;
  const isEmpty = count === 0;

  return (
    <div
      className={`relative rounded-xl border ${c.border} ${c.bg} px-3 py-2.5 ${
        isEmpty ? 'opacity-50' : ''
      }`}
    >
      {discount && !isEmpty && (
        <span className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-[#C4724F] text-white text-[9px] font-bold rounded-full shadow">
          −5 %
        </span>
      )}
      <div className="flex items-baseline justify-between gap-1.5">
        <span className={`font-display text-base sm:text-lg font-bold ${c.year} leading-none`}>
          {year}
        </span>
        <span className={`text-[10px] uppercase tracking-wider ${c.label}`}>Saison</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className={`text-lg sm:text-xl font-bold ${c.count} tabular-nums leading-none`}>
          {count}
        </span>
        <span className="text-[10px] text-slate-500">
          {count > 1 ? 'départs' : 'départ'}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500 truncate">
        {next ? `Prochain : ${formatShortDate(next.start_date)}` : isEmpty ? 'Bientôt' : '—'}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Section
// ────────────────────────────────────────────────────────
const DestinationsSection = () => {
  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCruises = async () => {
      try {
        const response = await axios.get(`${API}/cruises`);
        setCruises(response.data || []);
      } catch (error) {
        console.error('Error fetching cruises:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCruises();
  }, []);

  // Sort: cabin/privatisation actives first, then by destination
  const sortedCruises = useMemo(() => {
    return [...cruises].sort((a, b) => {
      const aHas = (a.dates || []).length > 0 ? 0 : 1;
      const bHas = (b.dates || []).length > 0 ? 0 : 1;
      return aHas - bHas;
    });
  }, [cruises]);

  return (
    <section
      id="destinations"
      className="relative py-16 sm:py-24 bg-gradient-to-b from-[#FAFAF7] via-[#F5F3EF] to-[#FAFAF7] overflow-hidden"
      data-testid="destinations-section"
    >
      {/* Subtle radial accent */}
      <div className="pointer-events-none absolute inset-0 opacity-30 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#2A9D8F]/8 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-[#C4724F]/10 border border-[#C4724F]/20">
            <span className="w-1.5 h-1.5 bg-[#C4724F] rounded-full animate-pulse" />
            <span className="text-[#C4724F] text-xs sm:text-sm font-semibold uppercase tracking-[0.16em]">
              Nos croisières en catamaran
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540] leading-tight mb-4">
            Choisissez votre <span className="italic text-[#2A9D8F]">aventure</span>
          </h2>
          <p className="text-[#4A5568] text-base sm:text-lg leading-relaxed">
            Sept destinations exceptionnelles. Planning{' '}
            <span className="font-semibold text-[#0A2540]">2027</span> disponible —
            réservez votre semaine en pension complète avec équipage.
          </p>
        </div>

        {/* Early bird banner */}
        <EarlyBirdBanner />

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-[480px] bg-white rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {sortedCruises.map((cruise) => (
              <CruiseCard key={cruise.slug || cruise.id} cruise={cruise} />
            ))}
          </div>
        )}

        {/* CTA bottom */}
        <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/destinations-croisieres-catamaran"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C4724F] hover:bg-[#B0613F] text-white font-semibold text-sm sm:text-base shadow-xl shadow-[#C4724F]/30 transition-all hover:scale-[1.02] w-full sm:w-auto"
            data-testid="all-destinations-btn"
          >
            Voir toutes les croisières
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-[#0A2540]/20 text-[#0A2540] font-semibold text-sm sm:text-base hover:bg-[#0A2540] hover:text-white transition-all w-full sm:w-auto"
          >
            Demande sur mesure
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DestinationsSection;
